import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

export type EditedOui = {
  oui: string;
  vendor: string;
  enabled: boolean;
};

type Props = {
  editedOui: EditedOui | 'new';
  onClose: () => void;
  onSaveSuccess: () => void;
};

export const OuiForm: FC<Props> = (props) => {
  const formMsg = useLocale().settings.oui.form;
  const api = useApi();
  const notifications = useNotifications();

  const methods = useCustomForm<EditedOui>({
    defaultValues: {
      oui: props.editedOui === 'new' ? '' : props.editedOui.oui,
      vendor: props.editedOui === 'new' ? '' : props.editedOui.vendor,
      enabled: props.editedOui === 'new' ? true : props.editedOui.enabled,
    },
  });

  const { control, handleSubmit, resetField, formState } = methods;

  const onSubmit: SubmitHandler<EditedOui> = async ({
    oui,
    vendor,
    enabled,
  }) => {
    if (props.editedOui === 'new') {
      try {
        await api.request({
          url: '/settings/oui',
          method: 'POST',
          data: { id: oui, enabled, vendor },
        });
        notifications.showNotification({
          type: 'success',
          message: formMsg.saveSuccess,
        });

        props.onSaveSuccess();
      } catch (e) {
        if (
          isIpfError(e, 'API') &&
          e.payload?.response.code === 'API_RESOURCE_CONFLICT'
        ) {
          notifications.showNotification({
            type: 'danger',
            message: formMsg.resourceConflict,
          });
          return;
        }
        throw e;
      }
    } else {
      await api.request({
        url: `/settings/oui/${props.editedOui.oui}`,
        method: 'PUT',
        data: { enabled, vendor },
      });
      notifications.showNotification({
        type: 'success',
        message: formMsg.updateSuccess,
      });

      props.onSaveSuccess();
    }
  };

  return (
    <FormProvider {...methods}>
      <SidePanel
        title={props.editedOui === 'new' ? 'Create new OUI' : 'Edit OUI'}
        closeOnBackdropClick={false}
        onClose={props.onClose}
        footer={({ closeModal }) => (
          <div className='flex gap-xs'>
            <Button
              type='submit'
              disabled={!formState.isDirty}
              loading={formState.isSubmitting}
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              className='flex-1'
              icon='fa-save'
            >
              {props.editedOui === 'new' ? 'Add OUI' : 'Save changes'}
            </Button>
            <Button variant='outline' onClick={closeModal} className='flex-1'>
              Close
            </Button>
          </div>
        )}
      >
        <form>
          <Card className='flex flex-col gap p-sm'>
            <FormTextField
              control={control}
              name='oui'
              type='text'
              label={formMsg.oui}
              onClear={
                formState.dirtyFields.oui
                  ? () => {
                      resetField('oui');
                    }
                  : undefined
              }
              rules={{
                required: formMsg.validations.required,
                minLength: {
                  value: 6,
                  message: formMsg.validations.ouiLength,
                },
                maxLength: {
                  value: 6,
                  message: formMsg.validations.ouiLength,
                },
              }}
              disabled={props.editedOui !== 'new'}
            />
            <FormTextField
              control={control}
              name='vendor'
              type='text'
              label={formMsg.vendor}
              onClear={
                formState.dirtyFields.vendor
                  ? () => {
                      resetField('vendor');
                    }
                  : undefined
              }
              rules={{
                required: formMsg.validations.required,
              }}
            />
            <FormSwitch
              control={control}
              name='enabled'
              description={formMsg.enabled}
            />
          </Card>
        </form>
      </SidePanel>
    </FormProvider>
  );
};
